import create from "zustand";
import { setStudentPersonalisation } from "js/api/personalisationApi";

/**
 * This store is a context store for information about the selected classroom.
 * Eventually when all our components are hooks, we might be able to remove this and just use react-query everywhere.
 * TODO: Add Zustand documentation for what a context store is
 */

type Student = {
	id: string;
	stableIdV1: string;
	firstName: string;
	lastName: string;
	username: string;
	pinned: boolean;
};

type Personalisation = {
	displayName: string;
	pinned: boolean;
	studentSortOrder: "firstNameAscending" | "lastNameAscending";
};

interface SelectedClassroomStoreData {
	id: string | null | undefined;
	stableIdV1: string | null | undefined;
	sourceId: string | null | undefined;
	name: string | null | undefined;
	students: Student[] | null | undefined;
	personalisation: Personalisation | null | undefined;
}

interface SelectedClassroomStoreActions {
	setData: (data: SelectedClassroomStoreData) => void;
	reset: () => void;
	findStudent: (username: string) => Student | null;
	toggleStudentPin: (teacherStableIdV1: string, username: string) => Promise<void>;
	sortUsersByPin: (users: { username: string }[]) => { username: string }[];
}

type SelectedClassroomStore = SelectedClassroomStoreData & SelectedClassroomStoreActions;

export const useSelectedClassroomStore = create<SelectedClassroomStore>((set, get) => ({
	id: null,
	stableIdV1: null,
	sourceId: null,
	name: null,
	students: null,
	personalisation: null,

	setData: (data: SelectedClassroomStoreData) => {
		set({ ...data });
	},

	// resets the store - used when classroom is exited
	reset: () => {
		set({ id: null, stableIdV1: null, sourceId: null, name: null, students: null, personalisation: null });
	},

	findStudent: (username: string) => {
		const students = get().students;
		if (!students) return null;
		return students.find((student) => student.username === username) || null;
	},

	toggleStudentPin: async (teacherStableIdV1: string, username: string) => {
		const classroomStableIdV1 = get().stableIdV1;
		if (!classroomStableIdV1) return;

		const student = get().findStudent(username);
		if (!student) return;
		const { stableIdV1, pinned } = student;

		await setStudentPersonalisation(teacherStableIdV1, classroomStableIdV1, stableIdV1, !pinned);

		// Update the store immediately with the new pinned value, without waiting for the API response
		set((state) => {
			const updatedStudents = state.students?.map((s) => {
				if (s.stableIdV1 === stableIdV1) {
					return { ...s, pinned: !s.pinned };
				}
				return s;
			});
			return { students: updatedStudents };
		});
	},

	sortUsersByPin: (users: { username: string }[]) =>
		users.sort((a, b) => {
			const aPinned = get().findStudent(a.username)?.pinned;
			const bPinned = get().findStudent(b.username)?.pinned;

			if (aPinned && !bPinned) return -1;
			if (!aPinned && bPinned) return 1;
			return 0;
		}),
}));
